import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import moment from "moment";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAirlineReprintData } from "./services/flightBooking.services";
import {
  calculateFareDetailsFromReprint,
  formatIndianNumber,
  getFlightImage,
  getRequestId,
} from "../../_helper/helper";
import { TfiArrowCircleDown } from "react-icons/tfi";
import {
  BsFillFileEarmarkPdfFill,
  BsFillFileTextFill,
  BsFillTicketPerforatedFill,
} from "react-icons/bs";
import { FaArrowLeft, FaEye } from "react-icons/fa6";
import PLANE from "../../assets/Images/plane.png";
import MALE_ICON from "../../assets/Images/user-male-circle.png";
import FEMALE_ICON from "../../assets/Images/user-female-circle.png";
import BRIEFCASE from "../../assets/Images/briefcase.png";
import BACKPACK from "../../assets/Images/backpack.png";
import { ErrorToast } from "../../components/toast/Toasts";
import { API_URL } from "../../app/config";
import { Button, DropDownButton } from "@progress/kendo-react-buttons";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog from "../../components/dialog/Dialog";
import { HiDownload } from "react-icons/hi";
import { RxCross2 } from "react-icons/rx";

const FlightBookingView: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { PortalBookingNo } = useParams();
  const loading = useAppSelector((state) => state.flightBooking.formLoading);
  const BookingDetails = useAppSelector(
    (state) => state.flightBooking.BookingDetails
  );
  const reqID = getRequestId();

  const [ticketLoading, setTicketLoading] = useState<any>(false);
  const [invoiceLoading, setInvoiceLoading] = useState<any>(false);
  const [activeFlightDetails, setactiveFlightDetails] = useState<number[]>([0]);
  const [invoiceLoadingIndexes, setInvoiceLoadingIndexes] = useState<number[]>(
    []
  );
  const [ticketLoadingIndexes, setTicketLoadingIndexes] = useState<number[]>(
    []
  );

  const isMultiPnr = BookingDetails?.AirPNRDetails?.length > 1;

  useEffect(() => {
    const payload = {
      Booking_RefNo: PortalBookingNo,
      Airline_PNR: "",
      Request_Id: reqID,
    };
    dispatch(getAirlineReprintData(payload));
  }, []);

  const card = {
    backgroundColor: "#fff",
    padding: "10px",
    marginBottom: "5px",
    borderRadius: "5px",
    boxshadow:
      "rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px",
    display: "flex",
    gap: "10px",
  };

  const faredetails = calculateFareDetailsFromReprint(
    BookingDetails?.AirPNRDetails || []
  );
  const leftContent = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: 0,
  };

  const items = [
    "Download Ticket",
    "Download Invoice",
    "Cancel Booking",
    // "View",
  ];

  const handleDownloadPdf = async (
    Booking_RefNo: string,
    Airline_PNR: string,
    index: number
  ) => {
    try {
      if (isMultiPnr) {
        setTicketLoadingIndexes((prev) => [...prev, index]);
      } else {
        setTicketLoading(true);
      }
      const Request_Id = getRequestId();

      const response = await fetch(`${API_URL}/AirlineReprint/Air_ReprintPDF`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Booking_RefNo,
          Airline_PNR,
          IsPayment: false,
          Request_Id,
        }),
      });

      if (!response.ok) {
        ErrorToast("Failed to generate PDF! - Please try after some time");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Ticket-${Airline_PNR}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      ErrorToast("Failed to download Ticket! - Please try again later");
    } finally {
      if (isMultiPnr) {
        setTicketLoadingIndexes((prev) => prev.filter((i) => i !== index));
      } else {
        setTicketLoading(false);
      }
    }
  };

  const handleDownloadInvoice = async (
    Booking_RefNo: string,
    Airline_PNR: string,
    index: number
  ) => {
    try {
      if (isMultiPnr) {
        setInvoiceLoadingIndexes((prev) => [...prev, index]);
      } else {
        setInvoiceLoading(true);
      }

      const Request_Id = getRequestId();

      const response = await fetch(
        `${API_URL}/AirlineReprint/Air_ReprintInvoice`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Booking_RefNo,
            Airline_PNR,
            IsPayment: false,
            Request_Id,
          }),
        }
      );

      if (!response.ok) {
        ErrorToast("Failed to generate PDF! - Please try after some time");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${Airline_PNR}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      ErrorToast("Failed to download Invoice! - Please try again later");
    } finally {
      if (isMultiPnr) {
        setInvoiceLoadingIndexes((prev) => prev.filter((i) => i !== index));
      } else {
        setInvoiceLoading(false);
      }
    }
  };

  const handleItemClick = (event: any) => {
    const clickedItem = event?.item;

    if (clickedItem === "Download Ticket") {
      // handleDownloadPdf(Booking_RefNo, Airline_PNR);
      if (!isMultiPnr) {
        !ticketLoading &&
          handleDownloadPdf(
            BookingDetails?.portal_booking_no,
            BookingDetails?.AirPNRDetails &&
              BookingDetails?.AirPNRDetails[0]?.Airline_PNR,
            0
          );
      } else {
        dispatch(openDialog("downloadTicket"));
      }
    } else if (clickedItem === "Download Invoice") {
      if (!isMultiPnr) {
        !invoiceLoading &&
          handleDownloadInvoice(
            BookingDetails?.portal_booking_no,
            BookingDetails?.AirPNRDetails &&
              BookingDetails?.AirPNRDetails[0]?.Airline_PNR,
            0
          );
      } else {
        dispatch(openDialog("downloadInvoice"));
      }
    } else if (clickedItem === "Cancel Booking") {
      navigate(
        `/flightbooking/cancel?bookingId=${BookingDetails?.portal_booking_no}`
      );
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <div className="container-fluid flight-reprint-view">
      <DownloadTicketDialog
        ticketLoadingIndexes={ticketLoadingIndexes}
        handleDownloadPdf={handleDownloadPdf}
      />
      <DownloadInvoiceDialog
        invoiceLoadingIndexes={invoiceLoadingIndexes}
        handleDownloadInvoice={handleDownloadInvoice}
      />
      <GridLayout
        style={{
          marginRight: 40,
          padding: "0 10px",
          // height: "100vh",
          // overflow: "hidden",
        }}
        gap={{ rows: 5, cols: 40 }}
        cols={[{ width: "50%" }, { width: "50%" }]}
      >
        {/* Flight Booking Header */}
        <GridLayoutItem
          colSpan={2}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="d-flex align-items-center mb-1">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-link me-3"
              style={{
                color: "#00a34d",
                background: "white",
                padding: "4px 6px 6px",
              }}
            >
              <FaArrowLeft size={20} />
            </button>
            <Typography.h4 style={{ margin: "0" }}>
              {"Flight Booking Details"}
            </Typography.h4>
          </div>
          <div>
            <DropDownButton
              popupSettings={{
                anchorAlign: {
                  horizontal: "right",
                  vertical: "bottom",
                },
                popupAlign: {
                  horizontal: "right",
                  vertical: "top",
                },
              }}
              disabled={invoiceLoading || ticketLoading}
              onItemClick={(e) => handleItemClick(e)}
              text="More Options"
              title="More Options"
              items={items}
              themeColor={"primary"}
            />
          </div>
        </GridLayoutItem>
        {/* Booking Number and Date */}

        <GridLayoutItem
          colSpan={2}
          style={{
            position: "sticky",
            background: "white",
            padding: "10px",
            borderRadius: "5px",
            boxShadow: "0 1px 6px 0 rgba(0, 0, 0, 0.2)",
            marginBottom: "10px",
          }}
        >
          <div className="row">
            <div className="col-6">
              <p className="fw-bold mb-1">
                Booking Ref. No:{" "}
                <strong style={{ color: "green" }}>
                  {BookingDetails?.Booking_RefNo}
                </strong>
              </p>
            </div>
            <div className="col-6">
              <Typography.h6 className=" text-end">
                Booking No:{" "}
                <strong style={{ color: "green" }}>
                  {BookingDetails?.portal_booking_no}
                </strong>
              </Typography.h6>
            </div>
            <div className="col-6">
              <Typography.p className="mb-0">
                Booked on:{" "}
                <strong>
                  {moment(
                    BookingDetails?.Booking_DateTime,
                    "DD/MM/YYYY HH:mm:ss"
                  ).format("DD-MMM-yyyy | h:mm:ss A")}
                </strong>
              </Typography.p>
            </div>
          </div>
        </GridLayoutItem>

        {/* Flight Details Title*/}

        <GridLayoutItem>
          <Typography.h5
            style={{
              margin: "0",
              padding: "5px",
            }}
          >
            {"Flight Details"}
          </Typography.h5>
        </GridLayoutItem>

        {/* Fare Details Title */}
        {/* 
        <GridLayoutItem>
          <Typography.h5 style={{ margin: "0", padding: "5px" }}>
            {"Fare Details"}
          </Typography.h5>
        </GridLayoutItem> */}

        {/* Flight Details Mapping */}

        <GridLayoutItem
          colSpan={2}
          style={
            {
              // overflowY: "scroll",
              // padding: "0 0 90px 0",
              // height: "65vh",
            }
          }
          className="viewBookingScroll"
        >
          <div className="col-12">
            {BookingDetails &&
              BookingDetails?.AirPNRDetails?.map(
                (tripInfo: any, tripIndex: any) => {
                  const priceDetails =
                    tripInfo?.Flights[0]?.Fares &&
                    tripInfo?.Flights[0]?.Fares[0];

                  const cancellationCharges =
                    (priceDetails?.FareDetails[0] &&
                      priceDetails?.FareDetails[0]?.CancellationCharges) ||
                    [];
                  const rescheduleCharges =
                    (priceDetails?.FareDetails[0] &&
                      priceDetails?.FareDetails[0]?.RescheduleCharges) ||
                    [];

                  return (
                    <div className="ticket-flight-details">
                      <div
                        className="flight-details-header"
                        onClick={() => {
                          setactiveFlightDetails((prevFlightDetails) => {
                            if (prevFlightDetails.includes(tripIndex)) {
                              return prevFlightDetails.filter(
                                (i) => i !== tripIndex
                              );
                            } else {
                              return [...prevFlightDetails, tripIndex];
                            }
                          });
                        }}
                      >
                        <h3 className="m-0">
                          {tripInfo?.Flights[0]?.Segments[0]?.Origin} →{" "}
                          {
                            tripInfo?.Flights[0]?.Segments[
                              tripInfo?.Flights[0]?.Segments?.length - 1
                            ]?.Destination
                          }
                        </h3>
                        <TfiArrowCircleDown
                          className="me-2 chevron-icon"
                          style={{
                            transform:
                              activeFlightDetails?.indexOf(tripIndex) >= 0
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                          }}
                        />
                      </div>
                      {activeFlightDetails?.indexOf(tripIndex) >= 0 && (
                        <div className="flight-details-content">
                          <>
                            {tripInfo?.Flights[0]?.Segments?.map(
                              (e: any, index: number) => {
                                const flightNo = e?.Flight_Number;
                                const flightCode = e?.Airline_Code;
                                const departureTime = e?.Departure_DateTime;
                                const arivalTime = e?.Arrival_DateTime;
                                const duration = e?.Duration;
                                const arivalCity = e?.Destination;
                                const departureCity = e?.Origin;
                                const arivalAirport = e?.Destination_Terminal;
                                const departureAirport = e?.Origin_Terminal;
                                const stops = e?.Stop_Over || 0;

                                const adultCheckingBaggage =
                                  tripInfo?.Flights[0]?.Fares[0]?.FareDetails[0]
                                    ?.Free_Baggage?.Check_In_Baggage;
                                const adultCabbinBaggage =
                                  tripInfo?.Flights[0]?.Fares[0]?.FareDetails[0]
                                    ?.Free_Baggage?.Hand_Baggage;
                                const FlightClass =
                                  tripInfo?.Flights[0]?.Fares[0]?.FareDetails[0]
                                    ?.FareClasses[index]?.Class_Desc;

                                return (
                                  <>
                                    <div className="flight-choose">
                                      <div className="fight-name">
                                        <img
                                          // src={e?.Airline_Name}
                                          src={getFlightImage(e?.Airline_Code)}
                                          alt=""
                                        />
                                        <span>
                                          <h5>{e?.Airline_Name}</h5>
                                          <p>
                                            {flightCode} - {flightNo}
                                          </p>
                                        </span>
                                      </div>
                                      <div className="route-timing">
                                        <div className="route-strating">
                                          <div className="ticket-view-time">
                                            {moment(departureTime).format(
                                              "HH:mm"
                                            )}
                                            <span>
                                              {", "}
                                              {moment(departureTime).format(
                                                "ddd MMM D"
                                              )}
                                            </span>
                                          </div>
                                          <div className="ticket-view-place">
                                            {departureCity}
                                          </div>
                                          <p>
                                            <span>
                                              Terminal{" : "}
                                              {departureAirport}
                                            </span>
                                          </p>
                                        </div>
                                        <div className="cunsume-time">
                                          <p>
                                            {stops === 0
                                              ? "Non Stop"
                                              : `${stops} Stop`}
                                          </p>
                                          <div className="cunsume-pic">
                                            <img alt={"PLANE"} src={PLANE} />
                                          </div>
                                          <strong>{duration}</strong>
                                        </div>
                                        <div className="route-strating route-arrival">
                                          <div className="ticket-view-time">
                                            {moment(arivalTime).format("HH:mm")}
                                            <span>
                                              {", "}
                                              {moment(arivalTime).format(
                                                "ddd MMM D"
                                              )}
                                            </span>
                                          </div>
                                          <div className="ticket-view-place">
                                            {arivalCity}
                                          </div>
                                          <p>
                                            <span>
                                              Terminal{" : "}
                                              {arivalAirport}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="route-bottom">
                                      <div className="fligt-parcel">
                                        <div className="low-parcel w-50">
                                          <BsFillTicketPerforatedFill
                                            style={{
                                              fontSize: 24,
                                              color: "#00a34d",
                                            }}
                                          />
                                          <span>
                                            <strong>{FlightClass}</strong>
                                          </span>
                                        </div>
                                        <div className="low-parcel w-50">
                                          <FaEye
                                            style={{
                                              fontSize: 24,
                                              color: "#00a34d",
                                            }}
                                            className="cursor-pointer"
                                            data-bs-toggle="modal"
                                            data-bs-target={`#farerulepopup_${tripIndex}`}
                                          />
                                          <span
                                            className="cursor-pointer"
                                            data-bs-toggle="modal"
                                            data-bs-target={`#farerulepopup_${tripIndex}`}
                                          >
                                            <strong className="text-dark">
                                              {"View Fare Rule"}
                                            </strong>
                                          </span>
                                        </div>
                                      </div>
                                      <div className="fligt-parcel">
                                        <div className="low-parcel w-50">
                                          <img
                                            src={BRIEFCASE}
                                            alt={"BRIEFCASE"}
                                          />
                                          <span>
                                            <strong>Cabin Baggage: </strong>
                                            {adultCabbinBaggage || ""} / Adult
                                          </span>
                                        </div>
                                        <div className="low-parcel w-50">
                                          <img
                                            src={BACKPACK}
                                            alt={"BACKPACK"}
                                          />
                                          <span>
                                            <strong>Check-In Baggage: </strong>
                                            {adultCheckingBaggage} / Adult
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                );
                              }
                            )}
                            <div className="traveller-container">
                              <div className="traveller-header">
                                <div className="column traveller-col">
                                  Traveller
                                </div>
                                <div className="column pnr-col">
                                  PNR/E-Ticket Number
                                </div>
                                <div className="column">Seat</div>
                                <div className="column">Meal</div>
                                <div className="column">Excess Baggage</div>
                              </div>

                              {tripInfo?.PAXTicketDetails?.map(
                                (pax: any, index: number) => (
                                  <div
                                    className={`traveller-row ${
                                      pax?.TicketStatus === "Tocancel" ||
                                      pax?.TicketStatus === "Cancelled"
                                        ? "cancelled-traveller-row"
                                        : ""
                                    }`}
                                    key={index}
                                  >
                                    <div className="column traveller-col">
                                      <img
                                        src={
                                          pax.Gender === 0
                                            ? MALE_ICON
                                            : FEMALE_ICON
                                        }
                                        alt="user"
                                      />
                                      <div className="traveller-name">
                                        <span>
                                          {`${pax.Title} ${pax.First_Name} ${pax.Last_Name}`}
                                        </span>
                                        {(pax?.TicketStatus === "Tocancel" ||
                                          pax?.TicketStatus ===
                                            "Cancelled") && (
                                          <p className="cancelled-name">
                                            {`(${pax?.TicketStatus})`}
                                          </p>
                                        )}
                                      </div>
                                      <div className="traveller-details">
                                        &nbsp;
                                        {pax?.Pax_type === 0
                                          ? "Adult"
                                          : pax?.Pax_type === 1
                                          ? "Child"
                                          : "Infant"}
                                        , {pax.Gender === 0 ? "Male" : "Female"}
                                      </div>
                                    </div>
                                    <div className="column pnr pnr-col">
                                      <span>{tripInfo?.Airline_PNR}/</span>
                                      <span>
                                        {pax?.TicketDetails[0]?.Ticket_Number}
                                      </span>
                                    </div>
                                    <div className="column seat">
                                      {pax?.SSRDetails?.find(
                                        (e: any) => e?.SSR_Type === 3
                                      )?.SSR_TypeDesc || " - "}
                                    </div>
                                    <div className="column meal">
                                      {pax?.SSRDetails?.find(
                                        (e: any) =>
                                          e?.SSR_Type === 1 || e?.SSR_Type === 2
                                      )?.SSR_TypeDesc || " - "}
                                    </div>
                                    <div className="column baggage">
                                      {pax?.SSRDetails?.find(
                                        (e: any) => e?.SSR_Type === 0
                                      )?.SSR_TypeDesc || " - "}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </>
                        </div>
                      )}
                      <div
                        className="modal fade"
                        id={`farerulepopup_${tripIndex}`}
                        data-bs-backdrop="static"
                        data-bs-keyboard="false"
                        tabIndex={-1}
                        aria-labelledby="farerulepopup"
                        aria-hidden="true"
                      >
                        <div
                          className="modal-dialog modal-dialog-centered"
                          style={{ maxWidth: 991 }}
                        >
                          <div className="modal-content">
                            <div className="modal-header">
                              <h4
                                className="modal-title fs-5"
                                id="edit-profile"
                              >
                                Fare Rule Information
                              </h4>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                              ></button>
                            </div>
                            <div className="modal-body travel-form-detail">
                              {/* <Loading loading={fareRulesLoading} /> */}
                              {cancellationCharges?.length <= 0 &&
                              rescheduleCharges?.length <= 0 ? (
                                <h4 className="text-center">
                                  Non - Refundable!!!
                                </h4>
                              ) : (
                                <>
                                  {cancellationCharges?.length > 0 && (
                                    <div className="cancel-wrapper">
                                      <div className="cancel-table-container">
                                        <table className="cancel-table">
                                          <thead>
                                            <tr>
                                              <th
                                                colSpan={5}
                                                className="cancel-th"
                                              >
                                                <h4>Cancellation Charges</h4>
                                              </th>
                                            </tr>
                                            <tr>
                                              <th className="cancel-th">#</th>
                                              <th className="cancel-th">
                                                Applicability
                                              </th>
                                              <th className="cancel-th">
                                                Passenger Type
                                              </th>
                                              <th className="cancel-th">
                                                Duration
                                              </th>
                                              <th className="cancel-th">
                                                Charge
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {cancellationCharges?.map(
                                              (cCharge: any, index: number) => {
                                                return (
                                                  <tr className="cancel-row">
                                                    <td className="cancel-td">
                                                      {index + 1}
                                                    </td>
                                                    <td className="cancel-td">
                                                      <span className="cancel-pill">
                                                        Per{" "}
                                                        {cCharge?.Applicablility ===
                                                        0
                                                          ? "Segment"
                                                          : "Passenger"}
                                                      </span>
                                                    </td>
                                                    <td className="cancel-td">
                                                      {cCharge?.PassengerType ===
                                                      0
                                                        ? "Adult"
                                                        : cCharge?.PassengerType ===
                                                          1
                                                        ? "Child"
                                                        : "Infant"}
                                                    </td>
                                                    <td className="cancel-td">
                                                      {cCharge?.DurationFrom}{" "}
                                                      {cCharge?.DurationTypeFrom ===
                                                      0
                                                        ? "Hr(s) "
                                                        : cCharge?.DurationTypeFrom ===
                                                          1
                                                        ? "days "
                                                        : ""}{" "}
                                                      – {cCharge?.DurationTo}{" "}
                                                      {cCharge?.DurationTypeTo ===
                                                      0
                                                        ? "Hr(s)"
                                                        : cCharge?.DurationTypeTo ===
                                                          1
                                                        ? "days"
                                                        : ""}
                                                    </td>
                                                    <td className="cancel-td">
                                                      {+cCharge?.Value === 0 ? (
                                                        "Non Chargeable"
                                                      ) : +cCharge?.Value ===
                                                          100 &&
                                                        cCharge?.ValueType ===
                                                          1 ? (
                                                        "Non Refundable"
                                                      ) : (
                                                        <>
                                                          {cCharge?.ValueType ===
                                                          0
                                                            ? "₹ "
                                                            : ""}
                                                          {cCharge?.Value}
                                                          {cCharge?.ValueType ===
                                                          1
                                                            ? "%"
                                                            : ""}
                                                        </>
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                            )}
                                            <tr className="cancel-row">
                                              <td
                                                className="cancel-td"
                                                colSpan={5}
                                              >
                                                <strong className="d-block">
                                                  *From the Time of Departure
                                                </strong>
                                                <strong>
                                                  *Sevice charge will be extra
                                                </strong>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                  {rescheduleCharges?.length > 0 && (
                                    <div className="cancel-wrapper">
                                      <div className="cancel-table-container">
                                        <table className="cancel-table">
                                          <thead>
                                            <tr>
                                              <th
                                                colSpan={5}
                                                className="cancel-th"
                                              >
                                                <h4>Reschedule Charges</h4>
                                              </th>
                                            </tr>
                                            <tr>
                                              <th className="cancel-th">#</th>
                                              <th className="cancel-th">
                                                Applicability
                                              </th>
                                              <th className="cancel-th">
                                                Passenger Type
                                              </th>
                                              <th className="cancel-th">
                                                Duration
                                              </th>
                                              <th className="cancel-th">
                                                Charge
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {rescheduleCharges?.map(
                                              (rCharge: any, index: number) => {
                                                return (
                                                  <tr className="cancel-row">
                                                    <td className="cancel-td">
                                                      {index + 1}
                                                    </td>
                                                    <td className="cancel-td">
                                                      <span className="cancel-pill">
                                                        Per{" "}
                                                        {rCharge?.Applicablility ===
                                                        0
                                                          ? "Segment"
                                                          : "Passenger"}
                                                      </span>
                                                    </td>
                                                    <td className="cancel-td">
                                                      {rCharge?.PassengerType ===
                                                      0
                                                        ? "Adult"
                                                        : rCharge?.PassengerType ===
                                                          1
                                                        ? "Child"
                                                        : "Infant"}
                                                    </td>
                                                    <td className="cancel-td">
                                                      {rCharge?.DurationFrom}{" "}
                                                      {rCharge?.DurationTypeFrom ===
                                                      0
                                                        ? "Hr(s) "
                                                        : rCharge?.DurationTypeFrom ===
                                                          1
                                                        ? "days "
                                                        : ""}{" "}
                                                      – {rCharge?.DurationTo}{" "}
                                                      {rCharge?.DurationTypeTo ===
                                                      0
                                                        ? "Hr(s)"
                                                        : rCharge?.DurationTypeTo ===
                                                          1
                                                        ? "days"
                                                        : ""}
                                                    </td>
                                                    <td className="cancel-td">
                                                      {+rCharge?.Value === 0 ? (
                                                        "Non Chargeable"
                                                      ) : +rCharge?.Value ===
                                                          100 &&
                                                        rCharge?.ValueType ===
                                                          1 ? (
                                                        "Non Refundable"
                                                      ) : (
                                                        <>
                                                          {rCharge?.ValueType ===
                                                          0
                                                            ? "₹ "
                                                            : ""}
                                                          {rCharge?.Value}
                                                          {rCharge?.ValueType ===
                                                          1
                                                            ? "%"
                                                            : ""}
                                                        </>
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                            )}
                                            <tr className="cancel-row">
                                              <td
                                                className="cancel-td"
                                                colSpan={5}
                                              >
                                                <strong className="d-block">
                                                  *From the Time of Departure
                                                </strong>
                                                <strong>
                                                  *Sevice charge will be extra
                                                </strong>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                              <div className="travel-detail-btns mt-4">
                                <a
                                  className="btn disable"
                                  href="javascript:void(0)"
                                  data-bs-dismiss="modal"
                                >
                                  Close
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
          </div>
          {/* {BookingDetails?.flight_details?.map(
            (f: any, index: number) => (
              <GridLayout
                style={flightCSS}
                key={index}
                gap={{ rows: 10 }}
                cols={[
                  { width: "15%" },
                  { width: "35%" },
                  { width: "15%" },
                  { width: "35%" },
                ]}
              >
                <GridLayoutItem>
                  <Typography.p style={content}>AIRLINE</Typography.p>
                  <Typography.h5 style={content}>
                    {f?.airline_name}
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Typography.p style={content}>DEPARTURE</Typography.p>
                  <Typography.h5 style={content}>
                    {f?.departure_place}
                  </Typography.h5>
                  <Typography.p style={content}>
                    {f?.departure_airport}
                  </Typography.p>
                </GridLayoutItem>
                <GridLayoutItem style={{ textAlign: "center" }}>
                  <Typography.h5 style={content}>NON-STOP</Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem style={{ textAlign: "end" }}>
                  <Typography.p style={content}>ARRIVAL</Typography.p>
                  <Typography.h5 style={content}>
                    {f?.arrival_place}
                  </Typography.h5>
                  <Typography.p style={content}>
                    {f?.arrival_airport}
                  </Typography.p>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Typography.p style={content}>CLASS</Typography.p>
                  <Typography.h5 style={content}>
                    {f?.flight_class}
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Typography.h5 style={content}>
                    {moment(f?.departure_date_time).format("DD-MMM-yyyy")}
                  </Typography.h5>
                  <Typography.h5 style={content}>
                    {moment(f?.departure_date_time).format("HH:mm")}
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem style={{ textAlign: "center" }}>
                  <Typography.p style={content}>DURATION</Typography.p>
                  <Typography.h5 style={content}>{f?.duration}</Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem style={{ textAlign: "end" }}>
                  <Typography.h5 style={content}>
                    {moment(f?.arrival_date_time).format("DD-MMM-yyyy")}
                  </Typography.h5>
                  <Typography.h5 style={content}>
                    {moment(f?.arrival_date_time).format("HH:mm")}
                  </Typography.h5>
                </GridLayoutItem>
              </GridLayout>
            )
          )} */}
        </GridLayoutItem>

        <GridLayoutItem>
          <Typography.h5
            style={{
              margin: "0",
              padding: "5px",
            }}
          >
            {"Flight Price Details"}
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem>
          <Typography.h5
            style={{
              margin: "0",
              padding: "5px",
            }}
          >
            {"Flight Payment Details"}
          </Typography.h5>
        </GridLayoutItem>

        <GridLayoutItem>
          <div className="price-card">
            {/* <h3 className="price-title m-0 border-bottom">
                    Price Breakup
                  </h3> */}
            <p className="border-bottom">
              <span className="price-smry-title">Airline Base Fare</span>
              <strong className="price-smry-amount">
                {BookingDetails &&
                  `₹ ${formatIndianNumber(
                    (faredetails?.adults?.basic || 0) +
                      (faredetails?.children?.basic || 0) +
                      (faredetails?.infants?.basic || 0)
                  )}`}
              </strong>
            </p>
            <p className="border-bottom">
              <span className="price-smry-title">Taxes & Fees</span>
              <strong className="price-smry-amount">
                {BookingDetails &&
                  `₹ ${formatIndianNumber(
                    (faredetails?.adults?.taxes || 0) +
                      (faredetails?.children?.taxes || 0) +
                      (faredetails?.infants?.taxes || 0) +
                      (faredetails?.overall?.serviceAmount || 0)
                  )}`}
              </strong>
            </p>
            {BookingDetails?.convenience_fee > 0 && (
              <p className="border-bottom">
                <span className="price-smry-title">Convenience Fee</span>
                <strong className="price-smry-amount">
                  {BookingDetails &&
                    `₹ ${formatIndianNumber(BookingDetails?.convenience_fee)}`}
                </strong>
              </p>
            )}
            <p className="border-bottom">
              <strong className="price-smry-amount">Total Amount</strong>
              <strong className="price-smry-amount">
                {BookingDetails &&
                  `₹ ${formatIndianNumber(
                    (faredetails?.adults?.total || 0) +
                      (faredetails?.children?.total || 0) +
                      (faredetails?.infants?.total || 0) +
                      (BookingDetails?.convenience_fee || 0)
                  )}`}
              </strong>
            </p>
            {BookingDetails?.PromoCodeValue > 0 && (
              <p className="border-bottom" style={{ color: "#00a34d" }}>
                <span className="price-smry-title">
                  PromoCode ({BookingDetails?.PromoCode})
                </span>
                <strong className="price-smry-amount">
                  {BookingDetails &&
                    `- ₹ ${formatIndianNumber(BookingDetails?.PromoCodeValue)}`}
                </strong>
              </p>
            )}
            {BookingDetails?.GiftCodeValue > 0 && (
              <p className="border-bottom" style={{ color: "#00a34d" }}>
                <span className="price-smry-title">
                  GiftCode ({BookingDetails?.GiftCode})
                </span>
                <strong className="price-smry-amount">
                  {BookingDetails &&
                    `- ₹ ${formatIndianNumber(BookingDetails?.GiftCodeValue)}`}
                </strong>
              </p>
            )}
            {BookingDetails?.wallet_balance > 0 && (
              <p className="border-bottom" style={{ color: "#00a34d" }}>
                <span className="price-smry-title">Used Wallet Amount</span>
                <strong className="price-smry-amount">
                  {BookingDetails &&
                    `- ₹ ${formatIndianNumber(BookingDetails?.wallet_balance)}`}
                </strong>
              </p>
            )}
            {BookingDetails?.CreditLimitUsedAmount > 0 && (
              <p className="border-bottom" style={{ color: "#00a34d" }}>
                <span className="price-smry-title">Used Credit Limit</span>
                <strong className="price-smry-amount">
                  {BookingDetails &&
                    `- ₹ ${formatIndianNumber(
                      BookingDetails?.CreditLimitUsedAmount
                    )}`}
                </strong>
              </p>
            )}
            <p>
              <strong className="price-smry-amount">Paid Amount</strong>
              <strong className="price-smry-amount">
                {BookingDetails &&
                  `₹ ${formatIndianNumber(
                    (faredetails?.adults?.total || 0) +
                      (faredetails?.children?.total || 0) +
                      (faredetails?.infants?.total || 0) +
                      (BookingDetails?.convenience_fee || 0) -
                      (BookingDetails?.PromoCodeValue || 0) -
                      (BookingDetails?.GiftCodeValue || 0) -
                      (BookingDetails?.wallet_balance || 0) -
                      (BookingDetails?.CreditLimitUsedAmount || 0)
                  )}`}
              </strong>
            </p>
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <div className="price-card">
            {/* <h3 className="price-title m-0 border-bottom">
                    Price Breakup
                  </h3> */}
            <p className="border-bottom">
              <span className="price-smry-title">Payment Mode</span>
              <strong className="price-smry-amount">
                {BookingDetails && BookingDetails?.PaymentMode}
              </strong>
            </p>
            <p className="border-bottom">
              <span className="price-smry-title">Payment Ref. No.</span>
              <strong className="price-smry-amount">
                {BookingDetails && BookingDetails?.PaymentRefNo}
              </strong>
            </p>
          </div>
        </GridLayoutItem>

        {/* Fare Summary */}

        <GridLayoutItem
          style={{
            overflowY: "scroll",
            minHeight: "30vh",
          }}
          className="viewBookingScroll"
        >
          {BookingDetails?.passenger_details?.map((p: any, index: number) => (
            <div
              key={index}
              style={{
                ...card,
                flexDirection: "column",
                padding: "2px 10px",
              }}
            >
              <Typography.p style={leftContent}>
                <strong>{`${p?.initial} ${p?.first_name} ${p?.last_name} - (${p?.gender})`}</strong>
                <strong> {`${p?.pax_type}`}</strong>
              </Typography.p>
            </div>
          ))}
        </GridLayoutItem>
      </GridLayout>
    </div>
  );
};

const DownloadTicketDialog = ({
  ticketLoadingIndexes,
  handleDownloadPdf,
}: {
  ticketLoadingIndexes: any[];
  handleDownloadPdf: any;
}) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const BookingDetails = useAppSelector(
    (state) => state.flightBooking.BookingDetails
  );

  return (
    <>
      {dialogName === "downloadTicket" && (
        <AppDialog>
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Typography.h5 className="m-0">
                {"Download E-Ticket(s)"}
              </Typography.h5>
              <Button
                themeColor={"error"}
                fillMode={"solid"}
                style={{ height: 30, width: 30 }}
                onClick={() => dispatch(closeDialog())}
              >
                <RxCross2 style={{ fontSize: 20 }} />
              </Button>
            </div>
            <GridLayout
              className="flight-reprint-view"
              style={{ minWidth: 450 }}
            >
              <GridLayoutItem>
                {BookingDetails &&
                  BookingDetails?.AirPNRDetails?.map(
                    (tripInfo: any, tripIndex: any) => {
                      return (
                        <div className="invoice-card">
                          <div className="icon">
                            <BsFillFileEarmarkPdfFill />
                          </div>
                          <div className="info">
                            <div className="title">
                              {tripInfo?.Flights[0]?.Segments[0]?.Origin} →{" "}
                              {
                                tripInfo?.Flights[0]?.Segments[
                                  tripInfo?.Flights[0]?.Segments?.length - 1
                                ]?.Destination
                              }
                            </div>
                            <div className="date">
                              {moment(
                                tripInfo?.Flights[0]?.Segments[0]
                                  ?.Departure_DateTime,
                                "MM/DD/YYYY HH:mm:ss"
                              ).format("D MMMM")}
                              ,{" "}
                              {tripInfo?.Flights[0]?.Segments[0]?.Airline_Name}
                            </div>
                          </div>
                          <div className="download">
                            {ticketLoadingIndexes?.includes(tripIndex) ? (
                              <div
                                className="spinner-border text-primary ticket-pop-loading"
                                role="status"
                              />
                            ) : (
                              <HiDownload
                                style={{ fontSize: 22 }}
                                onClick={() => {
                                  !ticketLoadingIndexes?.includes(tripIndex) &&
                                    handleDownloadPdf(
                                      BookingDetails?.portal_booking_no,
                                      tripInfo?.Airline_PNR,
                                      tripIndex
                                    );
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                {/* <DialogCloseButton themeColor="error" /> */}
                <Button
                  fillMode={"solid"}
                  themeColor={"primary"}
                  onClick={async () => {
                    if (!BookingDetails || !BookingDetails.AirPNRDetails)
                      return;

                    try {
                      await Promise.all(
                        BookingDetails.AirPNRDetails.map(
                          (tripInfo: any, tripIndex: number) =>
                            handleDownloadPdf(
                              BookingDetails?.portal_booking_no,
                              tripInfo?.Airline_PNR,
                              tripIndex
                            ).catch((err: any) => {
                              console.error(
                                `Download failed for trip ${tripIndex}`,
                                err
                              );
                            })
                        )
                      );
                    } catch (err) {
                      console.error("Error downloading all tickets", err);
                    }
                  }}
                  type="button"
                >
                  Download All Tickets
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const DownloadInvoiceDialog = ({
  invoiceLoadingIndexes,
  handleDownloadInvoice,
}: {
  invoiceLoadingIndexes: any[];
  handleDownloadInvoice: any;
}) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const BookingDetails = useAppSelector(
    (state) => state.flightBooking.BookingDetails
  );

  return (
    <>
      {dialogName === "downloadInvoice" && (
        <AppDialog>
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Typography.h5 className="m-0">
                {"Download Invoice(s)"}
              </Typography.h5>
              <Button
                themeColor={"error"}
                fillMode={"solid"}
                style={{ height: 30, width: 30 }}
                onClick={() => dispatch(closeDialog())}
              >
                <RxCross2 style={{ fontSize: 20 }} />
              </Button>
            </div>
            <GridLayout
              className=" flight-reprint-view"
              style={{ minWidth: 450 }}
            >
              <GridLayoutItem>
                {BookingDetails &&
                  BookingDetails?.AirPNRDetails?.map(
                    (tripInfo: any, tripIndex: any) => {
                      return (
                        <div className="invoice-card">
                          <div className="icon invoice">
                            <BsFillFileTextFill />
                          </div>
                          <div className="info">
                            <div className="title">
                              {tripInfo?.Flights[0]?.Segments[0]?.Origin} →{" "}
                              {
                                tripInfo?.Flights[0]?.Segments[
                                  tripInfo?.Flights[0]?.Segments?.length - 1
                                ]?.Destination
                              }
                            </div>
                            <div className="date">
                              {moment(
                                tripInfo?.Flights[0]?.Segments[0]
                                  ?.Departure_DateTime,
                                "MM/DD/YYYY HH:mm:ss"
                              ).format("D MMMM")}
                              ,{" "}
                              {tripInfo?.Flights[0]?.Segments[0]?.Airline_Name}
                            </div>
                          </div>
                          <div className="download">
                            {invoiceLoadingIndexes?.includes(tripIndex) ? (
                              <div
                                className="spinner-border text-primary ticket-pop-loading"
                                role="status"
                              />
                            ) : (
                              <HiDownload
                                style={{ fontSize: 22 }}
                                onClick={() => {
                                  !invoiceLoadingIndexes?.includes(tripIndex) &&
                                    handleDownloadInvoice(
                                      BookingDetails?.portal_booking_no,
                                      tripInfo?.Airline_PNR,
                                      tripIndex
                                    );
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20,
                }}
              >
                {/* <DialogCloseButton themeColor="error" /> */}
                <Button
                  fillMode={"solid"}
                  themeColor={"primary"}
                  onClick={async () => {
                    if (!BookingDetails || !BookingDetails.AirPNRDetails)
                      return;

                    try {
                      await Promise.all(
                        BookingDetails.AirPNRDetails.map(
                          (tripInfo: any, tripIndex: number) =>
                            handleDownloadInvoice(
                              BookingDetails?.portal_booking_no,
                              tripInfo?.Airline_PNR,
                              tripIndex
                            ).catch((err: any) => {
                              console.error(
                                `Download failed for trip ${tripIndex}`,
                                err
                              );
                            })
                        )
                      );
                    } catch (err) {
                      console.error("Error downloading all Invoices", err);
                    }
                  }}
                  type="button"
                >
                  Download All Tickets
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default FlightBookingView;
